import { GatsbyImage, GatsbyImageProps, getSrcSet, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useMemo } from 'react';

import { Box, Chip, Stack } from '@mui/material';
import { Link } from './link';

export interface HomepageLink {
  id: string
  href: string
  url: string
  text: string
}

interface ContentfulAssetImageProps
  extends Omit<Omit<GatsbyImageProps, "alt">, "image"> {
  asset: ContentfulAsset
  alt?: string
  aboveFold?: boolean
  skipGatsbyImage?: boolean
  width?: string,
  height?: string,
  contentfulPreviewProps?: any
  svgStyle?: React.CSSProperties
}

export type ContentfulAsset = {
  id: string
  title?: string
  url?: string
  contentful_id: string
  gatsbyImageData?: IGatsbyImageData
  file?: {
    contentType?: string
    url?: string
  }
  svg?: any
  localFile?: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData
    }
  }
}

/// Supports SVGs, childImageSharp is null for SVGs
export function ContentfulAssetImage({
  asset,
  loading,
  alt,
  style,
  imgStyle,
  aboveFold,
  width,
  height,
  contentfulPreviewProps,
  title,
  skipGatsbyImage,
  svgStyle
}: ContentfulAssetImageProps) {
  const imageData = asset.localFile?.childImageSharp?.gatsbyImageData || asset.gatsbyImageData
  // const imageData = asset.localFile ? getImage(asset.localFile) : (asset.gatsbyImageData ? getImage(asset.gatsbyImageData) : undefined)

  // if (skipGatsbyImage) {
  //   console.log('heree', asset, imageData)
  // }

  const dimensions = {}
  if (width) {
    dimensions['width'] = width
  }
  if (height) {
    dimensions['height'] = height
  }
  const isSvg = asset.file?.contentType === 'image/svg+xml'

  return !isSvg && (!skipGatsbyImage && asset && imageData) ? (
    <GatsbyImage
      loading={loading || (aboveFold ? "eager" : "lazy")}
      image={imageData}
      title={title || asset.title}
      // width={width}
      // height={height}
      alt={alt || asset.title}
      imgClassName={aboveFold ? "above-fold" : undefined}
      imgStyle={aboveFold ? { ...style, ...imgStyle, transition: '', willChange: '', ...dimensions } : { ...style, ...imgStyle, ...dimensions }}
      {...contentfulPreviewProps}
    />
  ) : asset && asset.url ? (
    <img
      style={{ ...(style || imgStyle), maxWidth: ((style?.maxWidth || imgStyle?.maxWidth) || (isSvg ? '100%' : undefined)), ...(isSvg ? svgStyle : undefined) }}
      width={imgStyle && imgStyle.width ? imgStyle.width : (width ? width : undefined)}
      height={imgStyle && imgStyle.height ? imgStyle.height : (height ? height : undefined)}
      src={(imageData?.images?.sources && imageData?.images?.sources.length > 0 ? imageData?.images?.sources[0].srcSet.split(' ')[0] : null) || imageData?.images?.fallback?.src || asset.url}
      srcSet={!isSvg ? getSrcSet(imageData) : undefined}
      loading={loading || (aboveFold ? "eager" : "lazy")}
      alt={alt || (asset && asset.title)}
      title={title || asset.title}
      {...contentfulPreviewProps}
    />
  ) : (
    <></>
  )
}
type KickerProps = {
  icon?: ContentfulAsset
  label: string
  background?: string
}

export function Kicker({ icon, background, ...props }: KickerProps) {
  // console.log('heree', icon.url)

  const label = useMemo(() => {
    if (!props.label) return '';
    if (props.label.endsWith('.')) return props.label.slice(0, -1);

    return props.label;
  }, [props.label])

  const largeLabel = label.length > 35

  return (
    <Box sx={{ display: { xs: largeLabel ? 'none' : 'initial', sm: largeLabel ? 'none' : 'initial', md: 'initial' } }}>
      <Chip
        icon={
          icon ? (
            <ContentfulAssetImage
              asset={icon}
              title={label}
              {...icon}
              imgStyle={{ height: "20px", width: "20px" }}
            />
          ) : undefined
        }
        label={label}
        sx={{
          padding: "0.5rem 1rem",
          background: background || "#F7F1FF",
          fontWeight: 500,
          color: "#722ED1",
          maxWidth: "calc(100vw - 2rem)",
          '.MuiChip-label': {
            overflow: 'initial'
          }
        }}
        variant="filled"
        color="primary"
      />
    </Box>
  )
}

interface ButtonListProps {
  links: HomepageLink[]
  buttonVariant?: "text" | "outlined" | "contained"
  buttonColor?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
}

export function ButtonList({
  links = [],
  buttonVariant,
  buttonColor
}: ButtonListProps) {
  if (!links || links.length === 0) return <></>

  return (
    <Stack direction='row' gap={2}
      alignItems={{ xs: 'center', sm: 'center', md: 'flex-start' }}
      justifyContent={{ xs: 'center', sm: 'center', md: 'flex-start' }}>
      {links && links.length > 0 ?
        links.map((link, i) => {
          return (
            <div key={link.id}>
              <Link url={link.href || link.url} variant={buttonVariant} color={buttonColor ? buttonColor : "inherit"}>
                {link.text}
              </Link>
            </div>
          )
        }) : <></>}
    </Stack>
  )
}
